import type { ReactNode } from 'react';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';

interface PcmpReportSectionProps {
  title: string;
  children: ReactNode;
}

/** A titled block of the printed report (legacy .sec / .sec-h). */
export function PcmpReportSection({ title, children }: PcmpReportSectionProps) {
  return (
    <section className="break-inside-avoid overflow-hidden rounded-sm border border-neutral-400">
      <h2 className="border-b border-neutral-400 bg-neutral-100 px-2 py-1 text-[10px] font-bold tracking-wide uppercase">
        {title}
      </h2>
      <div className="flex flex-col gap-2 p-2">{children}</div>
    </section>
  );
}

export interface PcmpReportField {
  label: string;
  value: ReactNode;
  isDanger?: boolean;
}

interface PcmpReportFieldsProps {
  fields: PcmpReportField[];
  /** Label/value pairs per row: 1 stacks them, 2 sets them side by side. */
  pairsPerRow?: 1 | 2;
}

const isBlank = (value: ReactNode) => value === null || value === undefined || value === '';

/** Label/value table (legacy table.kv). */
export function PcmpReportFields({ fields, pairsPerRow = 2 }: PcmpReportFieldsProps) {
  const rows: PcmpReportField[][] = [];
  for (let index = 0; index < fields.length; index += pairsPerRow) {
    rows.push(fields.slice(index, index + pairsPerRow));
  }
  return (
    <table className="w-full table-fixed border-collapse text-[11px]">
      <tbody>
        {rows.map((row) => (
          <tr key={row.map((field) => field.label).join('|')} className="align-top">
            {row.map((field, index) => (
              <FieldCells
                key={field.label}
                field={field}
                labelWidth={pairsPerRow === 1 ? 'w-[32%]' : 'w-[22%]'}
                // A lone last pair spans the empty cells beside it.
                span={index === row.length - 1 ? (pairsPerRow - row.length) * 2 + 1 : 1}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface FieldCellsProps {
  field: PcmpReportField;
  span: number;
  labelWidth: string;
}

function FieldCells({ field, span, labelWidth }: FieldCellsProps) {
  return (
    <>
      <td className={cn('py-0.5 pr-2 text-neutral-600', labelWidth)}>{field.label}</td>
      <td
        colSpan={span}
        className={cn('py-0.5 pr-2 font-medium break-words', field.isDanger && 'text-red-700')}
      >
        {isBlank(field.value) ? EMPTY_VALUE : field.value}
      </td>
    </>
  );
}
